import { Feature } from '@ngb-frontend/shared/types'

import { getCountryServiceProfile } from './getCountryServiceProfile'

import type {
  BusinessRuleContext,
  CountryServiceRulesProfile,
  EnabledServices,
  QueryCountryCode,
  ResolvedServiceRules,
  ServiceOptionValue,
  ServiceRulesQuery,
  ServiceValue,
} from '@ngb-frontend/shared/types'

/** APP_SERVICES.services */
const MAIN_SERVICES: ServiceValue[] = ['RegularMaintenance', 'Repair', 'MOT']

/**
 * Resolve which main / extra services are displayed and enabled for the given
 * selection. Reproduces the legacy driver (MnR) and agent (MnRDCC) filtering
 * and interdependency trees from a single place, branching only on the
 * country's service-rule profile — never on country identifiers.
 */
export const resolveServiceRules = (
  ctx: BusinessRuleContext,
  query: ServiceRulesQuery,
): ResolvedServiceRules => {
  // The service classification follows the resolved country config (the source
  // the legacy `is` helper used), falling back to the session country
  const profile = getCountryServiceProfile(
    ctx.countryConfig?.countryCode ?? ctx.countryCode,
  )
  const journey = query.journey ?? ctx.journey ?? Feature.MnR
  const additionalServices = ctx.countryConfig?.additionalServices ?? {}
  const hasMalfunctions =
    Object.keys(ctx.countryConfig?.malfunctions ?? {}).length > 0
  const services = query.selection.services ?? []
  const hasSelectedExtraServices = !!query.selection.extraServices?.length

  const displayed = resolveDisplayedMain(
    journey,
    profile,
    additionalServices,
    hasMalfunctions,
    query.usesVehicleCatalog,
  )

  const enabledMain = resolveEnabledMain(
    journey,
    profile,
    services,
    hasSelectedExtraServices,
    hasMalfunctions,
    query.usesVehicleCatalog,
    query.vehicleMainServices,
  )

  const enabledExtra = resolveEnabledExtra(
    journey,
    profile,
    additionalServices,
    services,
    enabledMain,
  )

  return {
    main: { displayed, enabled: enabledMain },
    extra: { enabled: enabledExtra },
  }
}

const resolveDisplayedMain = (
  journey: Feature,
  profile: CountryServiceRulesProfile,
  additionalServices: EnabledServices,
  hasMalfunctions: boolean,
  usesVehicleCatalog?: boolean,
): ServiceValue[] => {
  // LeasePlan driver flow offers the full static catalogue unfiltered
  if (journey === Feature.MnR && usesVehicleCatalog) return [...MAIN_SERVICES]

  const filtered =
    additionalServices.MOT === true
      ? MAIN_SERVICES
      : MAIN_SERVICES.filter((service) => service !== 'MOT')

  // Repair needs the service to be offered (Finland: not) and the country to
  // have malfunction codes to choose from
  return !profile.repairOffered || !hasMalfunctions
    ? filtered.filter((service) => service !== 'Repair')
    : filtered
}

const resolveEnabledMain = (
  journey: Feature,
  profile: CountryServiceRulesProfile,
  services: ServiceOptionValue[],
  hasSelectedExtraServices: boolean,
  hasMalfunctions: boolean,
  usesVehicleCatalog?: boolean,
  vehicleMainServices?: EnabledServices,
): EnabledServices => {
  if (journey === Feature.MnRDCC) {
    const enabled: EnabledServices = usesVehicleCatalog
      ? vehicleMainServices || {}
      : {
          RegularMaintenance: !profile.isDefaultRuleset
            ? true
            : !services.includes('MOT'),
          Repair: !profile.isDefaultRuleset ? true : !services.includes('MOT'),
          MOT: !profile.isDefaultRuleset
            ? true
            : !services.includes('Repair') &&
              !services.includes('RegularMaintenance') &&
              !hasSelectedExtraServices,
          EOL: true,
        }
    return applyExclusiveMain(profile.mainServicesExclusive, enabled, services)
  }

  // Driver flow: static enablement (or vehicle catalogue for LeasePlan) then
  // the Default-ruleset MOT ↔ main-service mutual exclusion
  const base: EnabledServices | undefined = usesVehicleCatalog
    ? vehicleMainServices
    : {
        RegularMaintenance: true,
        Repair: hasMalfunctions,
        MOT: true,
        EOL: true,
      }

  return applyExclusiveMain(
    profile.mainServicesExclusive,
    applyMainMutualExclusion(profile.isDefaultRuleset, base, services),
    services,
  )
}

/** ServiceForm's Default-country MOT ↔ Repair/RegularMaintenance exclusion */
const applyMainMutualExclusion = (
  isDefaultRuleset: boolean,
  enabled: EnabledServices | undefined,
  services: ServiceOptionValue[],
): EnabledServices => {
  if (!isDefaultRuleset) return enabled || {}

  return Object.entries(enabled || {}).reduce<EnabledServices>(
    (acc, [srv, isEnabled]) => {
      if (
        ((srv === 'Repair' || srv === 'RegularMaintenance') &&
          services.includes('MOT')) ||
        (srv === 'MOT' &&
          (services.includes('Repair') ||
            services.includes('RegularMaintenance')))
      ) {
        return { ...acc, [srv]: false }
      }
      return { ...acc, [srv]: isEnabled }
    },
    {},
  )
}

/**
 * Countries with exclusive main services allow one main service per booking:
 * once one is selected, every other main service is disabled. A selected
 * service stays enabled so it can always be deselected.
 */
const applyExclusiveMain = (
  mainServicesExclusive: boolean,
  enabled: EnabledServices,
  services: ServiceOptionValue[],
): EnabledServices => {
  const mainServices: ServiceOptionValue[] = MAIN_SERVICES
  if (
    !mainServicesExclusive ||
    !services.some((srv) => mainServices.includes(srv))
  ) {
    return enabled
  }

  return Object.entries(enabled).reduce<EnabledServices>(
    (acc, [srv, isEnabled]) => {
      const service = srv as ServiceOptionValue
      return {
        ...acc,
        [service]:
          mainServices.includes(service) && !services.includes(service)
            ? false
            : isEnabled,
      }
    },
    {},
  )
}

const resolveEnabledExtra = (
  journey: Feature,
  profile: CountryServiceRulesProfile,
  additionalServices: EnabledServices,
  services: ServiceOptionValue[],
  enabledMain: EnabledServices,
): EnabledServices => {
  if (journey === Feature.MnRDCC) {
    return resolveDccEnabledExtra(
      profile,
      additionalServices,
      services,
      enabledMain,
    )
  }

  // Driver flow: Austria/Switzerland keep only the replacement vehicle when the
  // driver picked MOT only; every other country keeps the offered extras
  const motOnly =
    !services.includes('RegularMaintenance') &&
    !services.includes('Repair') &&
    services.includes('MOT')

  if (motOnly && profile.motExtraServicePolicy === 'austriaSwitzerland') {
    return Object.keys(additionalServices).reduce<EnabledServices>(
      (acc, srv) => ({
        ...acc,
        [srv]: srv === 'ReplacementVehicle',
      }),
      {},
    )
  }

  return additionalServices
}

const resolveDccEnabledExtra = (
  profile: CountryServiceRulesProfile,
  additionalServices: EnabledServices,
  services: ServiceOptionValue[],
  enabledMain: EnabledServices,
): EnabledServices => {
  const hasEnabledMain = Object.values(enabledMain).filter(Boolean).length > 0
  const entries = Object.entries(additionalServices)
  const motSelected = services.includes('MOT')
  const motOnly =
    motSelected &&
    !services.includes('Repair') &&
    !services.includes('RegularMaintenance')

  if (profile.isDefaultRuleset || profile.motExtraServicePolicy === 'romania') {
    return entries.reduce<EnabledServices>((acc, [srv, isEnabled]) => {
      if (!hasEnabledMain) return { ...acc, [srv]: false }

      const isExtraKey =
        srv === 'ReplacementVehicle' ||
        srv === 'PickupAndReturn' ||
        srv === 'ComplementaryCleaning'
      if (
        (isExtraKey &&
          motSelected &&
          profile.motExtraServicePolicy === 'sweden') ||
        (motOnly && profile.motExtraServicePolicy === 'romania')
      ) {
        return { ...acc, [srv]: false }
      }
      return { ...acc, [srv]: isEnabled }
    }, {})
  }

  return entries.reduce<EnabledServices>((acc, [srv, isEnabled]) => {
    if (!hasEnabledMain) return { ...acc, [srv]: false }

    if (
      profile.motExtraServicePolicy !== 'none' &&
      (srv === 'PickupAndReturn' || srv === 'ComplementaryCleaning') &&
      motSelected
    ) {
      return { ...acc, [srv]: motOnly ? false : isEnabled }
    }
    return { ...acc, [srv]: isEnabled }
  }, {})
}

/**
 * Whether the country still offers an extra service when only MOT is selected
 * (Austria/Switzerland keep the replacement vehicle). Consumed by the extra
 * services step-skip predicate so it never branches on country identifiers.
 */
export const offersExtrasForMotOnly = (ctx: BusinessRuleContext): boolean => {
  const { motExtraServicePolicy } = getCountryServiceProfile(ctx.countryCode)
  return (
    motExtraServicePolicy === 'austriaSwitzerland' ||
    motExtraServicePolicy === 'none'
  )
}

/**
 * Apply the Default-ruleset MOT ↔ Repair/RegularMaintenance mutual exclusion
 * and the exclusive main services rule to a base enablement map against the
 * live form selection. Used by the driver ServiceForm so it reacts to selection
 * changes without branching on country identifiers — both flags live in the
 * country's profile.
 */
export const applyMainServiceExclusion = (
  countryCode: QueryCountryCode | undefined,
  enabled: EnabledServices | undefined,
  services: ServiceOptionValue[] = [],
): EnabledServices => {
  const profile = getCountryServiceProfile(countryCode)
  return applyExclusiveMain(
    profile.mainServicesExclusive,
    applyMainMutualExclusion(profile.isDefaultRuleset, enabled, services),
    services,
  )
}
